import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, from } from 'rxjs';
import { map, catchError, switchMap, tap, mergeMap } from 'rxjs/operators';
import {
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as AuthActions from './auth.actions';
import { UserService } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { getFirebaseAuthMessageKey } from '../../utils/firebase-auth-errors';
import { SUPER_ADMIN_TENANT_STORAGE_KEY } from '../../guards/super-admin-tenant-context.guard';
import { AppState } from '../app.state';
import { shouldUseMobileHub } from '../../pwa/pwa-runtime.util';

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private auth = inject(Auth);
  private router = inject(Router);
  private store = inject(Store<AppState>);
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private translate = inject(TranslateService);
  private injector = inject(Injector);

  // Login effect: Firebase Auth -> Backend API -> Store
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap(({ email, password }) =>
        from(
          runInInjectionContext(this.injector, () =>
            signInWithEmailAndPassword(this.auth, email, password)
          )
        ).pipe(
          mergeMap(async (userCredential) => {
            const token = await userCredential.user.getIdToken();
            localStorage.setItem('firebaseToken', token);
            return userCredential.user.uid;
          }),
          switchMap(() =>
            this.userService.getCurrentUser().pipe(
              map((user) => AuthActions.loginSuccess({ user })),
              catchError((error) =>
                of(AuthActions.loginFailure({ error: error.message || 'Failed to load user data' }))
              )
            )
          ),
          catchError((error) =>
            of(
              AuthActions.loginFailure({
                error: this.translate.instant(getFirebaseAuthMessageKey(error)),
              })
            )
          )
        )
      )
    )
  );

  // Register effect: Firebase Auth -> Backend API -> Store
  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.register),
      switchMap(({ email, password, createUserDto }) =>
        from(
          runInInjectionContext(this.injector, () =>
            createUserWithEmailAndPassword(this.auth, email, password)
          )
        ).pipe(
          mergeMap(async (userCredential) => {
            const token = await userCredential.user.getIdToken();
            localStorage.setItem('firebaseToken', token);
            // Assicura che firebaseUid corrisponda
            return {
              ...createUserDto,
              firebaseUid: userCredential.user.uid,
            };
          }),
          switchMap((updatedDto) =>
            this.userService.register(updatedDto).pipe(
              map((user) => AuthActions.registerSuccess({ user })),
              catchError((error) =>
                of(AuthActions.registerFailure({ error: error.message || 'Registration failed' }))
              )
            )
          ),
          catchError((error) =>
            of(
              AuthActions.registerFailure({
                error: this.translate.instant(getFirebaseAuthMessageKey(error)),
              })
            )
          )
        )
      )
    )
  );

  // Load user effect: Backend API -> Store
  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUser),
      switchMap(() =>
        this.userService.getCurrentUser().pipe(
          map((user) => AuthActions.loadUserSuccess({ user })),
          catchError((error) =>
            of(AuthActions.loadUserFailure({ error: error.message || 'Failed to load user' }))
          )
        )
      )
    )
  );

  // Navigate after successful login/register - redirect based on role
  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess, AuthActions.registerSuccess),
        tap((action) => {
          const user = action.user;
          if (user.role === 'super_admin') {
            this.router.navigate(['/admin']);
          } else if (user.role === 'waiter') {
            if (typeof window !== 'undefined' && shouldUseMobileHub()) {
              this.router.navigate(['/mobile/floor-plan/live']);
            } else {
              this.router.navigate(['/floor-plan/live']);
            }
          } else if (typeof window !== 'undefined' && shouldUseMobileHub()) {
            this.router.navigate(['/mobile']);
          } else {
            this.router.navigate(['/dashboard']);
          }
        })
      ),
    { dispatch: false }
  );

  // Navigate after loading user (on page refresh) - redirect only when needed.
  // Usa window.location.pathname perché al refresh router.url può non essere ancora aggiornato.
  // Admin/operator: comportamento invariato. Super admin: può restare su /dashboard se ha tenant (store o localStorage).
  loadUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loadUserSuccess),
        tap((action) => {
          const user = action.user;
          const pathname =
            typeof window !== 'undefined' ? window.location.pathname : this.router.url;
          const isOnAdmin = pathname.startsWith('/admin');
          const isRoot = pathname === '/' || pathname === '';

          // Solo sulla root: reindirizza in base al ruolo
          if (isRoot) {
            if (user.role === 'super_admin') {
              this.router.navigate(['/admin']);
            } else if (user.role === 'waiter') {
              if (typeof window !== 'undefined' && shouldUseMobileHub()) {
                this.router.navigate(['/mobile/floor-plan/live']);
              } else {
                this.router.navigate(['/floor-plan/live']);
              }
            } else if (typeof window !== 'undefined' && shouldUseMobileHub()) {
              this.router.navigate(['/mobile']);
            } else {
              this.router.navigate(['/dashboard']);
            }
            return;
          }
          // Super admin su route parte admin (es. /dashboard): consentito solo se ha tenant; altrimenti /admin
          if (user.role === 'super_admin' && !isOnAdmin) {
            const saved =
              typeof localStorage !== 'undefined' &&
              localStorage.getItem(SUPER_ADMIN_TENANT_STORAGE_KEY);
            if (saved) {
              this.store.dispatch(AuthActions.setAdminContextTenant({ tenantId: saved }));
              return;
            }
            this.router.navigate(['/admin']);
            return;
          }
          // Utente non super_admin su /admin: porta in dashboard
          if (user.role !== 'super_admin' && isOnAdmin) {
            this.router.navigate(['/dashboard']);
          }
          // Altrimenti resta sulla pagina corrente (es. /admin/tenants dopo refresh)
        })
      ),
    { dispatch: false }
  );

  // Persistenza e pulizia tenant di contesto super admin (localStorage)
  persistSuperAdminTenantContext$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.setAdminContextTenant),
        tap(({ tenantId }) => {
          if (typeof localStorage === 'undefined') return;
          if (tenantId) {
            localStorage.setItem(SUPER_ADMIN_TENANT_STORAGE_KEY, tenantId);
          } else {
            localStorage.removeItem(SUPER_ADMIN_TENANT_STORAGE_KEY);
          }
        })
      ),
    { dispatch: false }
  );

  // Navigate after logout (pulizia contesto tenant super admin da localStorage)
  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(async () => {
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem(SUPER_ADMIN_TENANT_STORAGE_KEY);
          }
          await this.authService.logout();
        })
      ),
    { dispatch: false }
  );

  // Forgot password effect: Firebase Auth sendPasswordResetEmail
  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      switchMap(({ email }) =>
        from(
          runInInjectionContext(this.injector, () => sendPasswordResetEmail(this.auth, email))
        ).pipe(
          map(() => AuthActions.forgotPasswordSuccess()),
          catchError((error) =>
            of(
              AuthActions.forgotPasswordFailure({
                error: this.translate.instant(getFirebaseAuthMessageKey(error)),
              })
            )
          )
        )
      )
    )
  );
}
